import React from "react"
import { useSelector } from "react-redux"
import { selectGenericState } from "@/store/features/genericSlice"
import {
  sanitizeTextForAnalytics,
  isCanadaSite,
  decodeHtmlCharacters,
} from "@/utils/helper"
import CONSTANTS from "@/constants"
import SwatchList from "@/components/ProductDetail/v3/ProductDetailView/SwatchList"
import usePdpi18n from "@/i18n/usePdpi18n"
import {
  getColorDataObject,
  getPdpLoaderPresetImageUrl,
  getPdpLoaderThumbs,
} from "@/components/ProductDetail/v3/productDetailsHelper"
import SuperSkuOption from "@/components/ProductDetail/v3/SuperSkuOption"
import style from "@/components/ProductDetail/v3/ProductDetailView/productDetailsView.module.scss"
import addToCartStyle from "@/components/ProductDetail/v3/AddToCart/index.module.scss"
import accordionStyles from "@/components/core/Accordion/Accordion.module.scss"
import productAccordionStyles from "@/components/ProductDetail/v3/ProductAccordionList/index.module.scss"
import VideoWrapper from "@/components/VideoWrapper/VideoWrapper"
import pdpVideoPlayIcon from "@/styles/images/play-button-overlay.svg"
import toolTipInfoIcon from "@/public/icons/tooltip-info.svg"
import Button from "@/components/core/Button/Button"
import Checkbox from "@/components/core/Checkbox/Checkbox"

const getInstallRequiredItems = productInfo => {
  const accessoryGroupSingle = productInfo?.["ProductRequiredAccessoryGroup_s"]
  const accessoryGroupMulti = productInfo?.["ProductRequiredAccessoryGroup_ss"]

  if (accessoryGroupSingle) return [accessoryGroupSingle]
  if (Array.isArray(accessoryGroupMulti)) return accessoryGroupMulti
  return []
}

const OVER_SIZED_FREIGHT_POLICY_TYPE = "4"
const NARRATIVE_DESCRIPTION_COLLAPSED_COUNT = 300

const Loader = props => {
  const {
    pdpData: productInfo,
    skuId,
    pdpJson,
    initialImgAlt,
    initialImgUrl,
  } = props || {}

  const {
    RegionBrandName_s: pdpBrnadName = "",
    ProductDescriptionProductShort_s: pdpShotDesc = "",
    ProductNarrativeDescription_s: narrativeDescription = "",
    sku_s: productID = "",
    ProductLocalCategory_s: productCategory = "",
    isCollapsedPDP_b: isCollapsedPDP = false,
    "Color.SKU.Details_ss": colorsDataList = [],
    facetFields = {},
    optionDocs = [],
    facetLabels = {},
  } = productInfo || {}

  const colorsDataObject = colorsDataList.map(item => getColorDataObject(item))
  const filteredColors = colorsDataObject.filter(
    item =>
      item.discontinued === false &&
      String(item.swatch || "").toLowerCase() !== "swatch_na"
  )
  const colorsDataObjectFormat = filteredColors.map(color => ({
    id: color.sku,
    name: "product-color",
    value: color.sku,
    label: color.name,
    colorUrl: CONSTANTS.COLOR_FILE_URL + color.swatch,
    checked: color.sku === skuId,
  }))

  const staticTexts = usePdpi18n(pdpJson, "", pdpBrnadName)
  const readMoreLabel = staticTexts?.descReadMore || "Read More"
  const isNarrativeDescriptionCollapsed =
    (narrativeDescription?.length ?? 0) > NARRATIVE_DESCRIPTION_COLLAPSED_COUNT
  const initImageAlt = initialImgAlt || pdpBrnadName || pdpShotDesc || ""
  const { globalConfig } = useSelector(selectGenericState)
  const {
    general: {
      swatchUrl = "",
      scene7AccountName = "PAWEB",
      disableCommerce = false,
      siteName: brandName = "",
    } = {},
  } = globalConfig || {}
  const { presetConfigs = "{}" } = pdpJson || {}
  const presetConfigObject = React.useMemo(() => {
    try {
      return JSON.parse(presetConfigs)
    } catch {
      return {}
    }
  }, [presetConfigs])
  const loaderPresetWidth =
    typeof window !== "undefined" ? window.innerWidth : 0

  const resolvedLoaderThumbs = getPdpLoaderThumbs({
    productInfo,
    skuId,
    initImage: initialImgUrl,
    initImageAlt,
    assetAccountName: scene7AccountName,
    swatchUrl,
    presetConfigObject,
    width: loaderPresetWidth,
  })

  const presetBasedMainSrc = getPdpLoaderPresetImageUrl({
    assetAccountName: scene7AccountName,
    swatchUrl,
    assetId: initialImgUrl || resolvedLoaderThumbs[0]?.assetId,
    presetConfigObject,
    width: loaderPresetWidth,
  })

  const mainLoaderImageSrc =
    presetBasedMainSrc || resolvedLoaderThumbs[0]?.src || ""
  const mainLoaderImageAlt = resolvedLoaderThumbs[0]?.label || initImageAlt
  const loaderVideoIds = (productInfo?.["ProductYoutubeID_s"] || "")
    .split("|")
    .map(id => id.trim())
    .filter(Boolean)

  const loaderThumbItems = [
    ...resolvedLoaderThumbs.map(item => ({
      type: "image",
      src: item.src,
      label: item.label,
    })),
    ...loaderVideoIds.map(videoId => ({
      type: "video",
      videoId,
    })),
  ]

  const visibleThumbs = loaderThumbItems.slice(0, 6)
  const remainingThumbCount = Math.max(loaderThumbItems.length - 6, 0)

  const installRequiredItems = getInstallRequiredItems(productInfo)
  const showInstallRequiredButton =
    installRequiredItems.length > 0 &&
    (!disableCommerce || isCanadaSite(brandName))
  const showRatingsShimmer = pdpJson?.disableRatings !== true
  const freightPolicyType =
    productInfo?.["ProductFreightPolicyType_s"]?.toString() || ""
  const showOversizedShippingMessage =
    freightPolicyType === OVER_SIZED_FREIGHT_POLICY_TYPE &&
    (!disableCommerce || isCanadaSite(brandName))
  const oversizedShippingMessage =
    staticTexts?.primaryTxt && staticTexts?.secondaryTxt
      ? `${staticTexts.primaryTxt} ${staticTexts.secondaryTxt}`
      : "This is a fragile or oversized item. Additional shipping charges may apply."

  const categoryTmp = sanitizeTextForAnalytics(productCategory)
  const category = categoryTmp
    ?.split(" ")
    .map(a => a.charAt(0).toUpperCase() + a?.slice(1))
    .join(" ")

  const normalizedFacetFields =
    facetFields && !Array.isArray(facetFields) ? facetFields : {}

  const accordionTitleMap = {
    Features: staticTexts?.productAccordionText?.features || "Features",
    Dimensions: staticTexts?.productAccordionText?.dimensions || "Dimensions",
    Specs: staticTexts?.productAccordionText?.specs || "Specs",
    "Installation & Support":
      staticTexts?.productAccordionText?.installationSupport ||
      "Installation & Support",
    "Design Files":
      staticTexts?.productAccordionText?.designFiles || "Design Files",
    "Available Services":
      staticTexts?.productAccordionText?.addOnServices || "Available Services",
  }
  const defaultAccordionKeys = [
    "Features",
    "Dimensions",
    "Specs",
    "Installation & Support",
    "Design Files",
  ]
  const accordionTitles =
    pdpJson?.accordianLabels?.length > 0
      ? pdpJson.accordianLabels.map(obj => {
          const key = decodeHtmlCharacters(obj?.value ?? "")
          return accordionTitleMap[key] || key
        })
      : defaultAccordionKeys.map(key => accordionTitleMap[key] || key)
  const activeSku = skuId || productID
  const currentSelectOptions =
    optionDocs.find(e => {
      const variantList = e?.["variantList.sku_ss"]
      if (!Array.isArray(variantList)) return false
      return variantList.includes(activeSku)
    }) ?? {}

  return (
    <div className={style.productDetailsV3Wrapper}>
      <div className="product-detail-page" data-testid="product-detail-page">
        <div className="container">
          <div className="header-section kf-react-plp-container">
            <div className="product-detail-page__breadcrumb">
              <nav aria-label="Breadcrumb">
                <ol>
                  <li>{" Home / "}</li>
                  <li>{category || "Parts"}</li>
                  <li className="adaopacity">
                    {" / "}
                    {category ? pdpBrnadName : "Parts"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="product-detail-page__print-share-container">
              <ul className="print-and-share list-none">
                <li>
                  <button
                    tabIndex="0"
                    className="gbh-data-layer"
                    data-gbh-data-layer=""
                    data-gbh-data-layer-custom=""
                    aria-label="Click here to print "
                  >
                    <img
                      src="/icons/print.svg"
                      alt=""
                      role="presentation"
                      width="17"
                      height="17"
                    />
                  </button>
                </li>
                <li>
                  <button
                    tabIndex="0"
                    aria-label="Click here to share "
                    className="share-button"
                  >
                    <img
                      src="/icons/share.svg"
                      alt=""
                      role="presentation"
                      width="17"
                      height="17"
                    />
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="product-overview print-overview">
            <div className="product-detail-page__image-gallery print-overview-left">
              <div className="product-detail-page__left-sticky">
                <div
                  className={`pdp-image-reel${
                    resolvedLoaderThumbs.length <= 1 ? " full-image-pdp" : ""
                  }`}
                >
                  {/* Thumbnail column */}
                  <div
                    className={`pdp-thumbnail-vertical${
                      resolvedLoaderThumbs.length < 6 ? " less-than-6" : ""
                    }`}
                  >
                    <div className="slider-pdp-thumbnail">
                      {visibleThumbs.map((item, i) => {
                        if (item.type === "video") {
                          return (
                            <div
                              key={`pdp-loader-video-thumb-${item.videoId}`}
                              className={`swiper-slide image-wrapper zoom-video-section pdp-loader-thumb-wrapper${
                                i === 0 ? " imageActiveThumb" : ""
                              }`}
                            >
                              <VideoWrapper
                                thumb
                                videoId={item.videoId}
                                alt={initImageAlt}
                              />
                              <span
                                className="zoom-video-icon"
                                aria-hidden="true"
                              >
                                <img
                                  src={pdpVideoPlayIcon?.src}
                                  alt=""
                                  tabIndex={-1}
                                />
                              </span>
                            </div>
                          )
                        }

                        return (
                          <div
                            key={`pdp-loader-thumb-${item.src}`}
                            className={`swiper-slide image-wrapper pdp-loader-thumb-wrapper${
                              i === 0 ? " imageActiveThumb" : ""
                            }`}
                          >
                            <img
                              src={item.src}
                              alt={item.label || initImageAlt}
                              width="60"
                              height="60"
                            />
                          </div>
                        )
                      })}
                    </div>
                    {remainingThumbCount > 0 ? (
                      <div className="image-gallery-view">
                        <p className="gallery-image-number">
                          +{remainingThumbCount}
                        </p>
                      </div>
                    ) : null}
                  </div>
                  {/* Main image column */}
                  <div
                    className={`pdp-image-reel__image-stack-view${
                      resolvedLoaderThumbs.length <= 1
                        ? " full-image-width"
                        : ""
                    }`}
                  >
                    <ul
                      className="pl-0 list-inline pdp-image-reel__image-stack-list"
                      id="image-gallery__image-stack-gallery"
                    >
                      <li className="pdp-image-reel__image-list">
                        <div className="pdp-image-reel__image-display pdp-loader-image-lock">
                          {mainLoaderImageSrc ? (
                            <img
                              alt={mainLoaderImageAlt}
                              src={mainLoaderImageSrc}
                              className="print"
                              loading="eager"
                              decoding="sync"
                              fetchPriority="high"
                            />
                          ) : (
                            <p className="shimmer-loading__para shimmer height-400" />
                          )}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="product-detail-page__content-section print-overview-right">
              <div className="row no-gutters product-detail-page__main">
                <div className="col-12">
                  <h1 className="product-detail-page__title-desc">
                    <span className="product-detail-page__title">
                      {pdpBrnadName}
                    </span>
                    <p className="product-detail-page__description">
                      {pdpShotDesc}
                    </p>
                  </h1>
                  <span className="product-detail-page__sku-id">
                    {"K-" + productID}
                  </span>
                </div>
              </div>

              <div className="row no-gutters product-detail-page__price-main">
                <div className="col-12 product-detail-page__price">
                  <div className="product-detail-page__price-value">
                    <div
                      className="product-detail-page__price-placeholder"
                      aria-hidden
                    >
                      <p className="shimmer-loading__para shimmer product-detail-page__price-placeholder-current"></p>
                      <p className="shimmer-loading__para shimmer product-detail-page__price-placeholder-reference"></p>
                    </div>
                  </div>
                </div>
              </div>

              {colorsDataObjectFormat.length > 0 ? (
                <SwatchList
                  colorsData={colorsDataObjectFormat}
                  isDiscontinued={false}
                  sku={skuId}
                  selectedColor=""
                  staticTexts={staticTexts}
                />
              ) : null}
              {isCollapsedPDP &&
              Object.keys(currentSelectOptions).length > 0 ? (
                <div className="row no-gutters">
                  <SuperSkuOption
                    i18n={staticTexts}
                    options={normalizedFacetFields}
                    curroptions={currentSelectOptions}
                    selectoption={() => {}}
                    facetLabels={facetLabels}
                    tooltipdata={{}}
                    selectMoreTooltipHandler={() => {}}
                    variantOptions={optionDocs}
                  />
                </div>
              ) : null}

              {showInstallRequiredButton ? (
                <div className="row no-gutters product-detail-page__install-item">
                  <div className="product-detail-page__install-item-btn">
                    <Button
                      role="button"
                      label={
                        staticTexts?.itemReqCTA || "Items required to install"
                      }
                      size="small"
                      type="ghost"
                    />
                  </div>
                </div>
              ) : null}

              {showOversizedShippingMessage ? (
                <div className="row no-gutters product-detail-page__oversized-msg-section">
                  <div className="col-12 oversized-msg-tooltip">
                    <span className="oversized-msg-primary-txt">
                      <img
                        className="oversized-msg-info-icon"
                        src={toolTipInfoIcon.src}
                        alt={staticTexts?.toolTip || "Info"}
                      />
                      <span>{oversizedShippingMessage}</span>
                    </span>
                  </div>
                </div>
              ) : null}

              <div className={addToCartStyle.addToCartWrapper}>
                <div className="row no-gutters buy-area">
                  <div className="add-to-cart col-12">
                    {pdpJson?.disableEcommerce === true &&
                    isCanadaSite(brandName) ? (
                      <div className="replace-add-to-cart-container">
                        <Button
                          className="replace-add-to-cart-button"
                          role="button"
                          ariaLabel={pdpJson.replaceAddToCartCtaLabel}
                          label={pdpJson.replaceAddToCartCtaLabel}
                          size="large"
                          type="primary"
                          flexible={true}
                        />
                      </div>
                    ) : (
                      <div className="add-cart-container">
                        <div className="quantity-selector-container">
                          <div className="quantity-selector">
                            <button
                              type="button"
                              aria-label="Decrease quantity"
                            >
                              <span className="plus">
                                <span className="line line-1"></span>
                              </span>
                            </button>
                            <input
                              className="quantity-selector__count"
                              type="number"
                              readOnly
                              value="1"
                              aria-label="Quantity"
                            />
                            <button
                              type="button"
                              aria-label="Increase quantity"
                            >
                              <span className="plus">
                                <span className="line line-1"></span>
                                <span className="line line-2"></span>
                              </span>
                            </button>
                          </div>
                        </div>
                        <div className="add-to-cart-container">
                          <Button
                            role="button"
                            ariaLabel={
                              staticTexts?.ariaLabel?.addProductToCart
                                ? staticTexts.ariaLabel.addProductToCart(
                                    pdpBrnadName || pdpShotDesc || "product"
                                  )
                                : "Add to Cart"
                            }
                            label={staticTexts?.addToCart || "Add to Cart"}
                            size="large"
                            type="primary"
                            flexible={true}
                          />
                        </div>
                      </div>
                    )}

                    <div className="bottom-section">
                      <div className="add-to-compare">
                        <Checkbox
                          value={staticTexts?.compareTxt || "Compare"}
                          checked={false}
                          onChange={() => {}}
                          checkBoxAria={staticTexts?.compareTxt || "Compare"}
                        />
                      </div>

                      <button type="button" className="favorites">
                        <img src="/icons/wish.svg" alt="Save to List" />
                        <span className="text">
                          {staticTexts?.saveToListText || "Save to List"}
                        </span>
                      </button>
                      {showRatingsShimmer ? (
                        <div
                          className="product-detail-page__inline-rating"
                          aria-hidden
                        >
                          <p
                            className="shimmer-loading__para shimmer width-170"
                            style={{ height: "24px", marginBottom: 0 }}
                          ></p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {narrativeDescription ? (
                <div className="product-detail-page__narrative-description">
                  <p
                    className={`product-detail-page__narrative-description${
                      isNarrativeDescriptionCollapsed ? "--show-more" : "--none"
                    }`}
                  >
                    {isNarrativeDescriptionCollapsed
                      ? narrativeDescription.slice(
                          0,
                          NARRATIVE_DESCRIPTION_COLLAPSED_COUNT
                        )
                      : narrativeDescription}
                    {isNarrativeDescriptionCollapsed && (
                      <span>
                        ...
                        <span
                          className="product-detail-page__narrative-description--show-more-button gbh-data-layer"
                          aria-hidden="true"
                        >
                          {readMoreLabel}
                        </span>
                      </span>
                    )}
                  </p>
                </div>
              ) : (
                <div className="product-detail-page__narrative-description height-70">
                  <p className="shimmer-loading__para shimmer"></p>
                  <p className="shimmer-loading__para shimmer"></p>
                </div>
              )}

              <div
                className={`${accordionStyles.accordionWrapper} ${productAccordionStyles.productAccordionListWrapper}`}
              >
                <div className="pdp-support-files__container product-specifications">
                  {accordionTitles.map((title, idx) => (
                    <div
                      className="pdp-support-files container-fluid no-print"
                      key={`pdp-loader-accordion-${idx}`}
                    >
                      <div className="pdp-support-files__content collapsible-accordion">
                        <div className="Collapsible is-closed">
                          <span className="Collapsible__trigger is-closed">
                            <div className="collapsible__header">
                              <span role="heading" aria-level="2">
                                {title}
                              </span>
                              <div className="plus" aria-hidden="true">
                                <div className="line line-1"></div>
                                <div className="line line-2"></div>
                              </div>
                            </div>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Loader
